import React, { useState } from 'react';

import { formatDateString } from '~libs/dayjs';
import { isPeriodRangeInvalid } from '~support-at-home/lib/contributionFilters';

import { Button } from '~components';
import DateInput from '~components/form-fields/inputs/DateInput';
import Icon, { faCalendar } from '~components/icon';
import PopOver from '~components/pop-over/PopOver';

interface ComponentProps {
  from: string | null;
  to: string | null;
  onChange: (period: { period_from: string | null; period_to: string | null }) => void;
}

export const ANY_PERIOD_LABEL = 'Any period';

export const formatPeriodFilter = (from: string | null, to: string | null): string => {
  if (from && to) return `${formatDateString(from)} - ${formatDateString(to)}`;
  if (from) return `From ${formatDateString(from)}`;
  if (to) return `Until ${formatDateString(to)}`;
  return ANY_PERIOD_LABEL;
};

const TARGET_ID = 'sah-client-contributions-filter-period';

// A field-like control that shows the chosen range and opens the From and To pickers in a popover,
// the same pattern as the Period editor in a row (PeriodEditor). Keeps the toolbar to one line.
const PeriodFilter: React.FC<ComponentProps> = ({ from, to, onChange }) => {
  const [isOpen, setOpen] = useState(false);
  const label = formatPeriodFilter(from, to);
  const isInvalid = isPeriodRangeInvalid({ period_from: from, period_to: to });

  return (
    <>
      <button
        id={TARGET_ID}
        type="button"
        className="contribution-period-trigger period-filter-trigger"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={`Filter by period: ${label}`}
        title={label}
        onClick={() => setOpen((open) => !open)}
      >
        <span
          className={`contribution-truncated-text ${from || to ? 'period-filter-value' : 'period-filter-placeholder'}`}
        >
          {label}
        </span>
        <span className="period-filter-indicator">
          <Icon icon={faCalendar} />
        </span>
      </button>
      {/* Mounted only while open: a closed PopOver still runs its own mount timers, which the rest of the
          page (and its tests) should not have to wait out. */}
      {isOpen && (
        <PopOver
          isOpen
          target={TARGET_ID}
          placement="bottom"
          autoFlip
          toggle={() => setOpen(false)}
          ariaLabel="Filter by period"
        >
          <div className="contribution-period-editor">
            <label>
              From
              <DateInput
                type="date"
                id="period_from"
                name="period_from"
                value={from}
                maxDate={to ?? undefined}
                onChange={(_name: string, value: string | null) => onChange({ period_from: value, period_to: to })}
              />
            </label>
            <label>
              To
              <DateInput
                type="date"
                id="period_to"
                name="period_to"
                value={to}
                minDate={from ?? undefined}
                onChange={(_name: string, value: string | null) => onChange({ period_from: from, period_to: value })}
              />
            </label>
            <Button size="sm" color="success" onClick={() => setOpen(false)}>
              Done
            </Button>
          </div>
          {isInvalid && (
            <div className="small text-danger mt-2" role="alert">
              From must be on or before To. The period is not applied until it is.
            </div>
          )}
        </PopOver>
      )}
    </>
  );
};

export default PeriodFilter;
